import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Globe2,
  MapPin,
  PlayCircle,
  Sparkles,
  UsersRound,
} from 'lucide-react';
import { companionCities } from './companionCities.data';
import CompanionsHeroCityCards from './CompanionsHeroCityCards';
import { formatFa, regionCount, regionFilters } from './constants';
import type { CompanionCity, CompanionRegionFilter } from './types';

type CompanionsHeroCopyProps = {
  activeCity: CompanionCity;
  activeRegion: CompanionRegionFilter;
  activeCityId: string;
  filteredCities: CompanionCity[];
  totalCompanions: number;
  filteredCount: number;
  onRegionChange: (region: CompanionRegionFilter) => void;
  onSelectCity: (cityId: string) => void;
  onOpenVideo: (city: CompanionCity) => void;
  onScrollToStories: () => void;
};

const fadeEase = [0.22, 1, 0.36, 1] as const;

const fadeUp = (delay: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay, ease: fadeEase },
});

const CompanionsHeroCopy: React.FC<CompanionsHeroCopyProps> = ({
  activeCity,
  activeRegion,
  activeCityId,
  filteredCities,
  totalCompanions,
  filteredCount,
  onRegionChange,
  onSelectCity,
  onOpenVideo,
  onScrollToStories,
}) => (
  <div className="companions-hero-frame companions-hero-copy-frame w-full text-center lg:text-right">
    <div className="companions-hero-copy__panel flex h-full min-h-0 flex-col overflow-hidden rounded-[var(--cp-radius-xl)] border border-slate-200/90 bg-white p-4 shadow-[var(--cp-shadow-soft)] sm:p-5 lg:p-6">
      <motion.div
        {...fadeUp(0)}
        className="relative flex shrink-0 flex-col items-center lg:items-start"
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-[11px] font-black text-[var(--cp-brand)]">
          <Sparkles className="h-3.5 w-3.5 text-[var(--cp-accent)]" />
          شبکه جهانی من حقیقی
        </span>

        <h1 className="mt-3 text-xl font-black leading-[1.2] text-[var(--cp-ink)] sm:text-[1.5rem] lg:text-[1.65rem]">
          <span className="block">همراهان ما</span>
          <span className="mt-0.5 block text-[var(--cp-brand)]">یک کره، هزار داستان تحول</span>
        </h1>
      </motion.div>

      <motion.div {...fadeUp(0.05)} className="relative flex min-h-0 flex-1 flex-col">
        <CompanionsHeroCityCards
          activeCity={activeCity}
          activeCityId={activeCityId}
          cities={filteredCities}
          onSelectCity={onSelectCity}
          onOpenVideo={onOpenVideo}
        />
      </motion.div>

      <motion.div {...fadeUp(0.12)} className="relative shrink-0 border-t border-slate-100 pt-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-[11px] font-black text-slate-700">فیلتر منطقه</p>
          {activeRegion !== 'همه' && (
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-black text-[var(--cp-brand)]">
              {formatFa(filteredCount)} شهر
            </span>
          )}
        </div>
        <div className="companions-region-scroll flex gap-1.5 overflow-x-auto pb-0.5 lg:flex-wrap lg:overflow-visible">
          {regionFilters.map((region) => {
            const active = activeRegion === region;
            return (
              <button
                key={region}
                type="button"
                onClick={() => onRegionChange(region)}
                className={`shrink-0 rounded-lg border px-3 py-1.5 text-[11px] font-black transition ${
                  active
                    ? 'border-[var(--cp-brand)] bg-[var(--cp-brand)] text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-[var(--cp-brand)]/30 hover:text-[var(--cp-brand)]'
                }`}
              >
                {region}
              </button>
            );
          })}
        </div>
      </motion.div>

      <motion.div
        {...fadeUp(0.18)}
        className="relative mt-3 shrink-0 grid grid-cols-3 divide-x divide-x-reverse divide-slate-100 overflow-hidden rounded-xl border border-slate-100 bg-slate-50"
      >
        {[
          { label: 'همراه', value: `+${formatFa(totalCompanions)}`, icon: UsersRound },
          { label: 'شهر', value: formatFa(companionCities.length), icon: MapPin },
          { label: 'منطقه', value: formatFa(regionCount), icon: Globe2 },
        ].map((stat) => (
          <div key={stat.label} className="px-1.5 py-2.5 text-center">
            <stat.icon className="mx-auto mb-1 h-3.5 w-3.5 text-[var(--cp-violet)]" />
            <p className="text-sm font-black tabular-nums text-[var(--cp-ink)]">{stat.value}</p>
            <p className="mt-0.5 text-[9px] font-bold text-slate-500">{stat.label}</p>
          </div>
        ))}
      </motion.div>

      <motion.div
        {...fadeUp(0.24)}
        className="relative mt-3 flex shrink-0 flex-col gap-2 sm:flex-row lg:justify-start"
      >
        <button
          type="button"
          onClick={onScrollToStories}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[var(--cp-brand)] px-4 py-2.5 text-xs font-black text-white transition hover:bg-[#173280] sm:text-sm"
        >
          داستان شهرها
          <ArrowLeft className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onOpenVideo(activeCity)}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-black text-slate-700 transition hover:border-[var(--cp-brand)]/30 hover:text-[var(--cp-brand)] sm:text-sm"
        >
          <PlayCircle className="h-3.5 w-3.5 text-[var(--cp-accent)]" />
          ویدیو
        </button>
      </motion.div>
    </div>
  </div>
);

export default CompanionsHeroCopy;
